import React, { useState, useEffect, useRef } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import { Sidebar } from './components/Sidebar';
import { TerminalPanel } from './components/TerminalPanel';
import { FakeFile, Folder, Settings } from './types';
import { projectLibrary } from './utils/projectLibrary';
import { usePyodide } from './hooks/usePyodide';
import appConfig from './config.json'; 
import { Play, RotateCcw } from 'lucide-react';

const generateId = () => Math.random().toString(36).substr(2, 9);

const App: React.FC = () => {
  // --- State ---
  const [settings] = useState<Settings>(appConfig);
  
  const [folders, setFolders] = useState<Folder[]>(() => {
    const saved = localStorage.getItem('folders');
    return saved ? JSON.parse(saved) : [];
  });
  const [files, setFiles] = useState<FakeFile[]>(() => {
    const saved = localStorage.getItem('files');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  
  const { output: terminalOutput, isRunning: isPythonRunning, isReady: isPythonReady, runPython, clearOutput } = usePyodide();

  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [terminalHeight, setTerminalHeight] = useState(200);
  const [isDraggingTerminal, setIsDraggingTerminal] = useState(false);
  const [isDraggingSidebar, setIsDraggingSidebar] = useState(false);

  const filesRef = useRef(files);
  const activeIdRef = useRef(activeFileId);
  const settingsRef = useRef(settings);
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);

  useEffect(() => { filesRef.current = files; }, [files]);
  useEffect(() => { activeIdRef.current = activeFileId; }, [activeFileId]);

  useEffect(() => {
    localStorage.setItem('folders', JSON.stringify(folders));
    localStorage.setItem('files', JSON.stringify(files));
  }, [folders, files]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingTerminal) {
        const newH = window.innerHeight - e.clientY - 22; 
        if (newH > 50 && newH < window.innerHeight - 100) setTerminalHeight(newH);
      }
      if (isDraggingSidebar) {
        const newW = e.clientX - 50; 
        if (newW > 150 && newW < 600) setSidebarWidth(newW);
      }
    };
    const handleMouseUp = () => { setIsDraggingTerminal(false); setIsDraggingSidebar(false); };
    if (isDraggingTerminal || isDraggingSidebar) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none'; 
    } else document.body.style.userSelect = 'auto';
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [isDraggingTerminal, isDraggingSidebar]);

  const handleCreateFolder = (parentId: string | null) => {
      const newFolder: Folder = { id: generateId(), parentId, name: 'New Folder', isOpen: true };
      setFolders(prev => [...prev, newFolder]);
      if (parentId) setFolders(prev => prev.map(f => f.id === parentId ? { ...f, isOpen: true } : f));
  };

  const handleCreateFile = (folderId: string | null) => {
      const newFile: FakeFile = {
          id: generateId(), folderId, name: 'Untitled', language: 'plaintext',
          content: '', script: '', progress: 0, isCompleted: false
      };
      setFiles(prev => [...prev, newFile]);
      setActiveFileId(newFile.id);
      if (folderId) setFolders(prev => prev.map(f => f.id === folderId ? { ...f, isOpen: true } : f));
  };

  const handleDeleteFile = (id: string) => {
      setFiles(prev => prev.filter(f => f.id !== id));
      if (activeFileId === id) setActiveFileId(null);
  };

  const handleDeleteFolder = (folderId: string) => {
      const getAllChildIds = (fId: string, all: Folder[]): string[] => {
          const kids = all.filter(f => f.parentId === fId);
          return [fId, ...kids.flatMap(k => getAllChildIds(k.id, all))];
      };
      const idsToDelete = getAllChildIds(folderId, folders);
      setFolders(prev => prev.filter(f => !idsToDelete.includes(f.id)));
      setFiles(prev => prev.filter(f => !f.folderId || !idsToDelete.includes(f.folderId)));
  };

  const handleToggleFolder = (folderId: string) => {
      setFolders(prev => prev.map(f => f.id === folderId ? { ...f, isOpen: !f.isOpen } : f));
  };

  const loadProject = (triggerName: string, targetFileId: string) => {
      const targetFile = filesRef.current.find(f => f.id === targetFileId);
      if (!targetFile) return;

      const project = projectLibrary.find(p => p.trigger === triggerName);
      if (!project) return;

      const { folderId } = targetFile;
      if (folderId) {
          setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: project.folderName } : f));
      }

      setFiles(prev => {
          const cleaned = prev.filter(f => {
              if (f.folderId === folderId && f.id !== targetFileId) return false; 
              return true; 
          });

          const firstProjFile = project.files[0];
          return cleaned.map(f => {
              if (f.id !== targetFileId) return f;
              return {
                  ...f,
                  name: firstProjFile.name,
                  language: firstProjFile.language,
                  script: firstProjFile.script,
                  content: `// Project: ${project.folderName}\n// Loading ${firstProjFile.name}...\n`,
                  progress: 0,
                  isCompleted: false
              };
          });
      });

      // FIX: Safer null check for setting model language
      if (monacoRef.current && editorRef.current) {
          const model = editorRef.current.getModel();
          if (model) {
            monacoRef.current.editor.setModelLanguage(model, project.files[0].language);
          }
      }
  };

  const checkForNextFile = (currentFile: FakeFile) => {
      const folder = folders.find(f => f.id === currentFile.folderId);
      if (!folder) return;

      const project = projectLibrary.find(p => p.folderName === folder.name);
      if (!project) return;

      const currentIndex = project.files.findIndex(pf => pf.name === currentFile.name);
      
      if (currentIndex !== -1 && currentIndex < project.files.length - 1) {
          const nextProjFile = project.files[currentIndex + 1];
          const exists = filesRef.current.find(f => f.folderId === currentFile.folderId && f.name === nextProjFile.name);
          if (exists) return;

          const newId = generateId();
          const nextFile: FakeFile = {
              id: newId, folderId: currentFile.folderId, name: nextProjFile.name,
              language: nextProjFile.language, script: nextProjFile.script,
              content: '', progress: 0, isCompleted: false
          };

          setFiles(prev => [...prev, nextFile]);
          setActiveFileId(newId);
      }
  };

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    monaco.languages.registerCompletionItemProvider('plaintext', {
        triggerCharacters: [':', ' '],
        provideCompletionItems: (model: any, position: any) => {
            const text = model.getValueInRange({
                startLineNumber: position.lineNumber, startColumn: 1,
                endLineNumber: position.lineNumber, endColumn: position.column
            });
            if (!text.match(/^#project:\s*$/)) return { suggestions: [] };

            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn
            };

            return { suggestions: projectLibrary.map(p => ({
                label: p.trigger, 
                kind: monaco.languages.CompletionItemKind.Folder, 
                insertText: p.trigger, 
                detail: `Load ${p.folderName}`,
                range: range 
            }))};
        }
    });

    editor.onKeyDown((e) => {
        const id = activeIdRef.current;
        if (!id) return;
        const file = filesRef.current.find(f => f.id === id);
        
        if (file && file.script) {
            const { charsPerPress } = settingsRef.current;
            if (e.ctrlKey || e.metaKey || e.altKey || e.code.startsWith('Arrow')) return;
            e.preventDefault(); e.stopPropagation();

            const direction = (e.code === 'Backspace' && e.shiftKey) ? -5 : 1;
            updateFileContent(id, file.progress + (charsPerPress * direction));
            
            // FIX: Safer null check for revealing line
            const model = editor.getModel();
            if (model) {
                editor.revealLine(model.getLineCount());
            }
        }
    });
  };

  const handleEditorChange = (value: string | undefined) => {
     if (!value || !activeFileId) return;
     const line = value.trim();
     if (line.startsWith('#project:')) {
         const trigger = line.replace('#project:', '').trim();
         loadProject(trigger, activeFileId);
     }
  };

  const updateFileContent = (id: string, newProgress: number) => {
    setFiles(prev => prev.map(f => {
      if (f.id !== id) return f;
      const newP = Math.max(0, Math.min(newProgress, f.script.length));
      const isFinished = newP >= f.script.length;
      if (isFinished && !f.isCompleted) setTimeout(() => checkForNextFile({ ...f, progress: newP }), 500);
      return { ...f, progress: newP, content: f.script.substring(0, newP), isCompleted: isFinished };
    }));
  };

  const handleRun = () => {
    const file = files.find(f => f.id === activeFileId);
    if (!file) return;

    if (file.folderId && file.name !== 'main.py' && file.name !== 'index.html') {
        if(file.language !== 'python') {
           clearOutput();
           terminalOutput.push(`<span style="color:#f87171">Error: Cannot execute ${file.language} files directly.</span>`);
           return;
        }
    }

    if (file.language === 'html') {
      const blob = new Blob([file.content], { type: 'text/html' });
      window.open(URL.createObjectURL(blob), '_blank');
    } else if (file.language === 'python') {
      if (!isPythonReady) {
          alert("Python is still loading. Please wait a moment.");
          return;
      }
      clearOutput();
      runPython(file.content, files, file.id);
    }
  };

  const handleShellCommand = (cmd: string) => {
      const trimmed = cmd.trim();
      if (trimmed === 'ls') {
          const fileNames = files.map(f => f.name).join('  ');
          terminalOutput.push(fileNames);
      } else if (trimmed.startsWith('python ')) {
          const fname = trimmed.split(' ')[1];
          const file = files.find(f => f.name === fname);
          if (file) {
              runPython(file.content, files, file.id);
          } else {
              terminalOutput.push(`<span style="color:#f87171">File not found: ${fname}</span>`);
          }
      } else {
           terminalOutput.push(`<span style="color:#666">Command not found: ${trimmed}</span>`);
      }
  };

  const activeFile = files.find(f => f.id === activeFileId);

  return (
    <div className="app-container">
      <div style={{ width: sidebarWidth + 50, display: 'flex', flexShrink: 0 }}>
        <Sidebar 
           folders={folders} files={files} activeFileId={activeFileId} 
           onSelectFile={setActiveFileId} onCreateFile={handleCreateFile} onCreateFolder={handleCreateFolder}
           onDeleteFile={handleDeleteFile} onDeleteFolder={handleDeleteFolder} onToggleFolder={handleToggleFolder}
        />
      </div>

      <div className="resizer-vertical" 
        style={{ width: '4px', cursor: 'col-resize', background: '#252526', borderRight: '1px solid #3e3e42' }}
        onMouseDown={() => setIsDraggingSidebar(true)}
      />

      <div className="main-area">
        <div className="editor-header">
           {activeFile && (
               <div style={{ padding: '0 15px', borderTop: '1px solid #007acc', background: '#1e1e1e', height: '100%', display:'flex', alignItems:'center', fontSize:'13px' }}>
                   {activeFile.name} 
               </div>
           )}
           <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, paddingRight: 10, fontSize: '12px' }}>
              <button className="btn text-green-500" onClick={handleRun} title="Run Script">
                 {isPythonRunning ? 'Running...' : <Play size={14} fill="currentColor"/>}
              </button>
              <button className="btn text-yellow-500" onClick={() => { 
                  localStorage.clear(); 
                  window.location.reload(); 
              }} title="Reset Environment"><RotateCcw size={14}/></button>
           </div>
        </div>

        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {activeFile ? (
                 <Editor height="100%" theme="vs-dark" path={activeFile.id} 
                    language={activeFile.script ? activeFile.language : 'plaintext'}
                    value={activeFile.content} onChange={handleEditorChange} onMount={handleEditorMount}
                    options={{ minimap: { enabled: false }, fontSize: 14, automaticLayout: true }} 
                 />
            ) : <div className="flex items-center justify-center h-full text-[#3e3e42]">Select a file...</div>}
        </div>

        <div className="resizer" onMouseDown={() => setIsDraggingTerminal(true)}></div>
        
        <div style={{ height: terminalHeight }} className="terminal-container">
            <TerminalPanel 
                output={terminalOutput} isRunning={isPythonRunning}
                onStop={() => { }}
                onClear={clearOutput} 
                onCommand={handleShellCommand}
            />
        </div>
        
        <div className="status-bar">
             <span>{isPythonReady ? "Python Ready" : "Loading Python..."}</span>
             <span>UTF-8</span>
        </div>
      </div>
    </div>
  );
};

export default App;